import { FormEvent, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import App from "./App";
import DocumentsWorkspace from "./DocumentsWorkspace";
import PartsWorkspace from "./PartsWorkspace";
import VisualWorkspace from "./VisualWorkspace";
import WalkdownWorkspace from "./WalkdownWorkspace";
import { getRemoteSession, loadRemotePlantState, signIn, signOut, signUp } from "./remoteData";
import { remoteDataEnabled, supabase } from "./supabaseClient";

const STORAGE_KEY = "plant-equipment-intelligence-v01";
type Workspace = "equipment" | "visual" | "parts" | "documents" | "walkdown";

export default function Bootstrap() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(!remoteDataEnabled);
  const [loading, setLoading] = useState(remoteDataEnabled);
  const [error, setError] = useState("");
  const [workspace, setWorkspace] = useState<Workspace>("equipment");

  useEffect(() => {
    if (!remoteDataEnabled || !supabase) return;
    let mounted = true;

    getRemoteSession().then((current) => {
      if (!mounted) return;
      setSession(current);
      if (!current) {
        setLoading(false);
        setReady(false);
      }
    }).catch((err) => {
      if (!mounted) return;
      setError(err instanceof Error ? err.message : "Unable to read session");
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        setReady(false);
        setLoading(false);
        setWorkspace("equipment");
      }
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!remoteDataEnabled || !session) return;
    let cancelled = false;
    setLoading(true);
    setError("");

    loadRemotePlantState().then((state) => {
      if (cancelled) return;
      if (!state) throw new Error("No remote plant state was returned.");
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      setReady(true);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Unable to load plant data");
      setReady(false);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [session?.user.id]);

  if (!remoteDataEnabled) return <App />;
  if (!session) return <AuthScreen loading={loading} error={error} />;
  if (loading || !ready) return <LoadingScreen error={error} />;

  return (
    <div className="remote-app-wrapper">
      <div className="remote-mode-banner">
        <span><strong>Secure database mode</strong> · authenticated transactional writes · RLS enforced</span>
        <div className="remote-nav-actions">
          <button className={`workspace-button ${workspace === "equipment" ? "active" : ""}`} onClick={() => setWorkspace("equipment")}>Equipment</button>
          <button className={`workspace-button ${workspace === "visual" ? "active" : ""}`} onClick={() => setWorkspace("visual")}>Visual</button>
          <button className={`workspace-button ${workspace === "walkdown" ? "active" : ""}`} onClick={() => setWorkspace("walkdown")}>Walkdown</button>
          <button className={`workspace-button ${workspace === "parts" ? "active" : ""}`} onClick={() => setWorkspace("parts")}>Parts &amp; Sourcing</button>
          <button className={`workspace-button ${workspace === "documents" ? "active" : ""}`} onClick={() => setWorkspace("documents")}>Documents &amp; Photos</button>
          <button className="workspace-button" onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
      {workspace === "equipment" ? <App /> : workspace === "visual" ? <VisualWorkspace /> : workspace === "walkdown" ? <WalkdownWorkspace /> : workspace === "parts" ? <PartsWorkspace /> : <DocumentsWorkspace />}
    </div>
  );
}

function LoadingScreen({ error }: { error: string }) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-mark">PE</div>
        <h1>Loading plant database</h1>
        <p>Retrieving secured equipment records and line structure.</p>
        {error ? <div className="auth-error">{error}</div> : <div className="auth-loading">Connecting…</div>}
      </div>
    </div>
  );
}

function AuthScreen({ loading, error }: { loading: boolean; error: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [localError, setLocalError] = useState("");
  const [busy, setBusy] = useState(false);

  async function perform(mode: "signin" | "signup") {
    setMessage("");
    setLocalError("");
    setBusy(true);
    try {
      const result = mode === "signin"
        ? await signIn(email.trim(), password)
        : await signUp(email.trim(), password);

      if (result.error) {
        setLocalError(result.error.message);
        return;
      }

      if (mode === "signup" && !result.data.session) {
        setMessage("Account created. Check your email if confirmation is required, then sign in.");
      }
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void perform("signin");
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={handleSubmit}>
        <div className="auth-mark">PE</div>
        <div className="auth-eyebrow">PLANT EQUIPMENT INTELLIGENCE</div>
        <h1>Technical Library</h1>
        <p>Sign in to the secured plant equipment database. The first account created becomes the system administrator.</p>

        <label>Email<input type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Password<input type="password" autoComplete="current-password" required minLength={8} value={password} onChange={(event) => setPassword(event.target.value)} /></label>

        {(error || localError) && <div className="auth-error">{localError || error}</div>}
        {message && <div className="auth-message">{message}</div>}

        <button className="auth-primary" type="submit" disabled={loading || busy}>Sign in</button>
        <button className="auth-secondary" type="button" disabled={loading || busy} onClick={() => void perform("signup")}>Create first / new account</button>

        <small>Production mode uses Supabase authentication and row-level security. Demo builds can still run locally without credentials.</small>
      </form>
    </div>
  );
}
